import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import herobg from "../../assests/hero-section.png"

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-48 pb-24 sm:pt-56 sm:pb-32 overflow-hidden"
    >
      {/* Background grid + glows */}
      <div className="absolute inset-0 grid-bg opacity-50" />
      <div className="absolute -top-40 -left-20 h-[480px] w-[480px] rounded-full bg-yellow-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 right-0 h-[420px] w-[420px] rounded-full bg-orange-500/15 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-400/30 bg-yellow-400/5 text-yellow-300 text-xs font-mono-ui mb-6"
            data-testid="hero-badge"
          >
            <Sparkles size={14} />
            <span className="tracking-[0.18em] uppercase">Marketing Labs · India</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tighter"
            data-testid="hero-headline"
          >
            Zesty solutions.
            <br />
            <span className="brand-gradient-text">Seamless tech.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-7 text-lg sm:text-xl text-zinc-400 max-w-2xl leading-relaxed"
            data-testid="hero-subhead"
          >
            End-to-end marketing for B2B, B2C and D2C brands. We blend fractional
            marketing expertise into sharp strategy, Ad creatives and campaigns that actually move
            numbers.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-yellow-400 text-zinc-950 font-bold hover:bg-yellow-300 transition-all hover:-translate-y-0.5 shadow-[0_10px_40px_-10px_rgba(250,204,21,0.6)]"
            >
              Get a free growth audit
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </a>
            <a
              href="#work"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/15 text-zinc-100 hover:border-yellow-400/50 hover:text-yellow-300 transition-all hover:-translate-y-0.5"
            >
              See our work
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-zinc-500"
          >
            <span className="inline-flex items-center gap-2">
              <ShieldCheck size={16} className="text-yellow-400" /> Fractional Marketing
            </span>
            <span className="inline-flex items-center gap-2">
              <ShieldCheck size={16} className="text-yellow-400" /> B2B · B2C ·
              D2C playbooks
            </span>
            <span className="inline-flex items-center gap-2">
              <ShieldCheck size={16} className="text-yellow-400" /> E-commerce StoreFronts
            </span>
          </motion.div>
        </div>

        {/* Visual side */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-zinc-900/40 p-1">
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/15 via-transparent to-orange-500/20" />
            <div className="relative rounded-[1.4rem] overflow-hidden">
              <img
                src={herobg}
                alt="Lemonsqueezy growth dashboard"
                className="w-full h-[420px] object-cover"
                data-testid="hero-visual"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/70 via-zinc-950/10 to-transparent" />
            </div>
          </div>

          {/* Floating stat cards */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="absolute -left-6 bottom-10  sm:flex flex-col bg-zinc-900/90 backdrop-blur-md border border-white/10 rounded-2xl px-5 py-4 shadow-xl"
          >
            <span className="text-xs text-zinc-400 font-mono-ui uppercase tracking-wider">
              Avg. ROAS lift
            </span>
            <span className="text-3xl font-bold brand-gradient-text mt-1">
              3.4×
            </span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="absolute -right-4 top-10  sm:flex flex-col bg-zinc-900/90 backdrop-blur-md border border-white/10 rounded-2xl px-5 py-4 shadow-xl"
          >
            <span className="text-xs text-zinc-400 font-mono-ui uppercase tracking-wider">
              Startups scaled up
            </span>
            <span className="text-3xl font-bold text-zinc-50 mt-1">20+</span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
